import React, { useCallback, useRef, useState, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { MessageSlider } from '../AutoDetect/MessageSlider';
import { useTutor } from '../../../contexts/TutorContext';
import { useAudioState } from '../../../contexts/AudioContext';
import { useChatContext } from '../../../contexts/ChatContext';
import { webRTCService } from '../../../services/WebRTCService';
import { processAudio } from '../../../api';
import './styles.css';

interface TapToTalkProps {
  onResponse: (text: string) => void;
  onError: (error: string) => void;
  isReady: boolean;
  sessionId: string;
}

export const TapToTalk: React.FC<TapToTalkProps> = ({
  onResponse,
  onError,
  isReady,
  sessionId,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [showMessages, setShowMessages] = useState(false);
  const { selectedTutor } = useTutor();
  const { isAudioPlaying } = useAudioState();
  const { messages, addMessage, setIsWaitingForAI } = useChatContext();
  const recordingTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Show messages when there are any
    if (messages.length > 0) {
      setShowMessages(true);
    }
  }, [messages]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartRecording = useCallback(async (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isReady || isProcessing || isRecording) return;

    try {
      await webRTCService.startRecording();
      setIsRecording(true);
      recordingTimer.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
      onError('Failed to start recording. Please check your microphone permissions.');
    }
  }, [isReady, isProcessing, isRecording, onError]);

  const handleStopRecording = useCallback(async (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isRecording || isProcessing) return;

    try {
      setIsProcessing(true);
      setIsWaitingForAI(true);
      
      if (recordingTimer.current) {
        clearInterval(recordingTimer.current);
        recordingTimer.current = null;
      }

      const audioBlob = await webRTCService.stopRecording();
      setIsRecording(false);
      setRecordingDuration(0);

      addMessage({
        text: 'Audio message sent...',
        type: 'user',
      });

      const response = await processAudio(audioBlob, sessionId, selectedTutor?.id || '');
      onResponse(response);
      
      addMessage({
        text: response,
        type: 'bot',
      });
    } catch (error) {
      console.error('Failed to process recording:', error);
      onError('Failed to process recording. Please try again.');
    } finally {
      setIsProcessing(false);
      setIsWaitingForAI(false);
    }
  }, [isRecording, isProcessing, sessionId, selectedTutor, onResponse, onError, addMessage, setIsWaitingForAI]);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  const handleMouseLeave = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isRecording && !isProcessing) {
      await handleStopRecording(e);
    }
  }, [isRecording, isProcessing, handleStopRecording]);

  return (
    <View style={styles.container}>
        <View style={styles.buttonContainer}>
          <div
            className={`mic-button ${isRecording ? 'recording' : ''} ${(!isReady || isProcessing) ? 'disabled' : ''}`}
            onMouseDown={handleStartRecording}
            onMouseUp={handleStopRecording}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleStartRecording}
            onTouchEnd={handleStopRecording}
            onContextMenu={handleContextMenu}
            style={styles.button}
          >
            <MaterialIcons
              name={isRecording ? 'mic' : 'mic-none'}
              size={24}
              color={(!isReady || isProcessing) ? '#999' : (isRecording ? '#ff4444' : '#ffffff')}
            />
          </div>
          {isRecording && (
            <div className="recording-duration" style={styles.duration}>
              {formatDuration(recordingDuration)}
            </div>
          )}
        </View>
      
       {showMessages && messages.length > 0 && (
        <View style={styles.messagesContainer}>
          <MessageSlider 
            messages={messages} 
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1a1a1a',
    flexDirection: 'column',
    position: 'relative',
    height: '100%',
  },
  messagesContainer: {
    flex: 1,
    minWidth: 280,
    maxWidth: '100%',
    position: 'relative',
    height: 300,
    minHeight: 300,
    padding: 20,
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    marginBottom: 20,
    '@media (max-width: 768px)': {
      minWidth: '100%',
      height: 250,
      minHeight: 250,
    },
  },
  controlsContainer: {
    padding: 20,
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
  },
  buttonContainer: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  button: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#4A90E2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'all 0.2s ease',
  },
  duration: {
    position: 'absolute',
    bottom: -20,
    fontSize: 12,
    color: '#666',
  },
});
